import {
  getAccountById,
  getEmployeeFormById,
  getEmployeePolicyById,
  getEmployeeDependentById,
  tryGetMedicalPlanById,
  tryGetDentalPlanById,
  tryGetVisionPlanById,
} from "EntitiesApi/Selectors";
import { formatNameFL } from "Utils/formatName";

import { MILLISECONDS_IN_DAY } from "Common/Constants";
import { formFormatDate } from "Common/forms/utils";

import type { MedicalPlan } from "Care/types";

import type { State } from "./createStore";
import type { EmployeeForm, EmployeeDependent, EmployeePolicy } from "Utils/types";

export const getPlan = (state: State, type: string) => {
  const employeePolicy = getAnyEmployeePolicy(state);
  if (type === "accident") return getAccidentPlan(state);
  if (type === "accidental_death") return getAccidentalDeathPlan(state);
  if (type === "dental") return tryGetDentalPlanById(state, employeePolicy.dentalPlan);
  if (type === "medical") return tryGetMedicalPlanById(state, employeePolicy.medicalPlan);
  if (type === "life") return getLifePlan(state);
  if (type === "long_term") return getDisabilityPlans(state, "long_term");
  if (type === "short_term") return getDisabilityPlans(state, "short_term");
  if (type === "term_life") return getTermLifePlan(state);
  if (type === "vision") return tryGetVisionPlanById(state, employeePolicy.visionPlan);
  if (type === "whole_life") return getWholeLifePlan(state);
  throw new Error("Invalid plan type");
};

export const getMedicalPlan = (state: State) => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const medicalPlan: MedicalPlan | null = tryGetMedicalPlanById(state, employeePolicy.medicalPlan);
  return medicalPlan;
};

export const isAdminMode = (state: State): boolean => state.adminMode;
export const isEnrolled = (state: State, employeePolicyId: string = ""): boolean => {
  const employeePolicy = employeePolicyId !== "" ? getEmployeePolicyById(state, employeePolicyId) : getAnyEmployeePolicy(state);
  return employeePolicy.isEnrolling === "Yes" || employeePolicy.isEnrollingDental === "Yes" || employeePolicy.isEnrollingVision === "Yes";
};
export const getEmployeeFormId = (state: State): string => state.employeeFormId;
export const getEmployeeFormByFormId = (state: State): EmployeeForm => {
  let employeeFormId = getEmployeeFormId(state);

  if (employeeFormId == "") {
    const account = getAccountById(state, state.accountId);
    const employeePolicyId = account.enrolledEmployeePolicy || account.upcomingEmployeePolicy;
    const employeePolicy = getEmployeePolicyById(state, employeePolicyId);
    employeeFormId = employeePolicy.employeeForm;
  }

  return getEmployeeFormById(state, employeeFormId);
};

export const getAnyEmployeePolicy = (state: State): EmployeePolicy => {
  const employeeFormId = getEmployeeFormId(state);
  let employeePolicy;

  if (employeeFormId == "") {
    const account = getAccountById(state, state.accountId);
    const employeePolicyId = account.enrolledEmployeePolicy || account.upcomingEmployeePolicy || account.cancelledEmployeePolicy.id;
    employeePolicy = getEmployeePolicyById(state, employeePolicyId);
  } else {
    const employeeForm = getEmployeeFormByFormId(state);
    const employeePolicyId = employeeForm.enrolledEmployeePolicy || employeeForm.upcomingEmployeePolicy || employeeForm.cancelledEmployeePolicy.id;
    employeePolicy = getEmployeePolicyById(state, employeePolicyId);
  }

  return employeePolicy;
};

export const isInitialDataLoaded = (state: State): boolean => state.initialDataLoaded;
export const planCarrier = (state: State): string => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const plan = tryGetMedicalPlanById(state, employeePolicy.medicalPlan);
  return plan ? plan.carrier : "";
};
export const coverageStartDate = (state: State): string | null | undefined => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const dateString = employeePolicy && employeePolicy.coverageStartDateAt;

  if (dateString) {
    return formFormatDate(new Date(dateString));
  }

  return "";
};

const getSubmissionDate = (state: State): string | null | undefined => {
  const employeePolicy = getAnyEmployeePolicy(state);
  return employeePolicy ? employeePolicy.submittedAt : "";
};

export const submissionDateMin = (state: State): string | null | undefined => {
  const dateString = getSubmissionDate(state);

  if (dateString) {
    const submissionDate = new Date(dateString);
    const lowerRangeDaysToAdd = 7;
    const lowerRangeSubmissionDate = new Date(submissionDate.setTime(submissionDate.getTime() + lowerRangeDaysToAdd * MILLISECONDS_IN_DAY));
    return formFormatDate(lowerRangeSubmissionDate);
  }

  return "";
};
export const submissionDateMax = (state: State): string | null | undefined => {
  const dateString = getSubmissionDate(state);

  if (dateString) {
    const submissionDate = new Date(dateString);
    const upperRangeDaysToAdd = 14;
    const upperRangeSubmissionDate = new Date(submissionDate.setTime(submissionDate.getTime() + upperRangeDaysToAdd * MILLISECONDS_IN_DAY));
    return formFormatDate(upperRangeSubmissionDate);
  }

  return "";
};
export const getEmployeeIdCardOptions = (state: State): Array<Array<string>> => {
  const employeeForm = getEmployeeFormByFormId(state);
  const dependents = getEmployeeFormDependents(state, employeeForm);
  const employeeIdCardOptions = [];
  employeeIdCardOptions.push(["employee", formatNameFL(employeeForm)]);
  dependents.forEach((dependent) => {
    employeeIdCardOptions.push([dependent.id, formatNameFL(dependent)]);
  });
  return employeeIdCardOptions;
};

const getEmployeeFormDependents = (s: State, employeeForm: EmployeeForm): Array<EmployeeDependent> =>
  employeeForm.employeeDependents.map((id) => getEmployeeDependentById(s, id)).filter((d) => d.active);

export const getDependents = (state: State): Array<EmployeeDependent> => {
  const employeeForm = getEmployeeFormByFormId(state);
  return getEmployeeFormDependents(state, employeeForm);
};

const getDependentsPlansForPolicy = (employeePolicy: EmployeePolicy): Record<string, any> => {
  const dependentsPlans = {};

  for (const dependentKeyMedical in employeePolicy.dependentsEnrolling) {
    if (employeePolicy.dependentsEnrolling[dependentKeyMedical]) {
      dependentsPlans[dependentKeyMedical] = dependentsPlans[dependentKeyMedical] || {};
      dependentsPlans[dependentKeyMedical].medical = true;
    }
  }

  for (const dependentKeyDental in employeePolicy.dependentsEnrollingDental) {
    if (employeePolicy.dependentsEnrollingDental[dependentKeyDental]) {
      dependentsPlans[dependentKeyDental] = dependentsPlans[dependentKeyDental] || {};
      dependentsPlans[dependentKeyDental].dental = true;
    }
  }

  for (const dependentKeyVision in employeePolicy.dependentsEnrollingVision) {
    if (employeePolicy.dependentsEnrollingVision[dependentKeyVision]) {
      dependentsPlans[dependentKeyVision] = dependentsPlans[dependentKeyVision] || {};
      dependentsPlans[dependentKeyVision].vision = true;
    }
  }

  return dependentsPlans;
};

export const getDependentsPlans = (state: State): Record<string, any> => {
  const employeePolicy = getAnyEmployeePolicy(state);
  return getDependentsPlansForPolicy(employeePolicy);
};
export const getPlanTypeMap = (state: State): Record<string, any> => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const planState = {
    medical: "Medical",
    dental: "Dental",
    vision: "Vision",
  };
  const planTypes = {};
  // construct plan for employee
  const employeePlans = [];

  if (employeePolicy.isEnrolling === "Yes") {
    employeePlans.push(["medical", planState.medical]);
  }

  if (employeePolicy.isEnrollingDental === "Yes") {
    employeePlans.push(["dental", planState.dental]);
  }

  if (employeePolicy.isEnrollingVision === "Yes") {
    employeePlans.push(["vision", planState.vision]);
  }

  planTypes.employee = employeePlans;
  // construct plan for dependents
  const dependentsPlans = getDependentsPlansForPolicy(employeePolicy);

  // construct plans into employeePlans
  for (const key in dependentsPlans) {
    if (dependentsPlans.hasOwnProperty(key)) {
      const currDependentPlan = [];

      if (dependentsPlans[key].medical) {
        currDependentPlan.push(["medical", planState.medical]);
      }

      if (dependentsPlans[key].dental) {
        currDependentPlan.push(["dental", planState.dental]);
      }

      if (dependentsPlans[key].vision) {
        currDependentPlan.push(["vision", planState.vision]);
      }

      planTypes[key] = currDependentPlan;
    }
  }

  return planTypes;
};
export type EnrolledInsuranceTypes = {
  medical: boolean;
  dental: boolean;
  vision: boolean;
};
export const getEnrolledInsuranceTypes = (state: State): EnrolledInsuranceTypes => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const enrolledInsuranceTypes = {
    medical: employeePolicy.isEnrolling === "Yes",
    dental: employeePolicy.isEnrollingDental === "Yes",
    vision: employeePolicy.isEnrollingVision === "Yes",
  };
  return enrolledInsuranceTypes;
};
export type EmployeeLink = {
  text: string;
  icon: string;
  href: string;
  disabled?: boolean;
  useRouter?: boolean;
};

/**
 * Updated Employee Link Groups
 * @see https://simplyinsured.atlassian.net/browse/FP-337
 * new: Individual Plans (no links yet, will add in near future)
 * removed: Reference, Personal Information
 */
export type EmployeeLinkGroup = "Benefits" | "Personal Information" | "Reference" | "Individual Plans" | "Annual Renewal";

export const getAccidentPlan = (state: State) => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const { accidentPlan } = employeePolicy;
  return accidentPlan;
};

export const getAccidentalDeathPlan = (state: State) => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const { accidentalDeathPlan } = employeePolicy;
  return accidentalDeathPlan;
};
export const getLifePlan = (state: State) => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const { lifePlan } = employeePolicy;
  return lifePlan;
};

export const getTermLifePlan = (state: State) => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const { termLifePlan } = employeePolicy;
  return termLifePlan;
};

export const getWholeLifePlan = (state: State) => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const { wholeLifePlan } = employeePolicy;
  return wholeLifePlan;
};
export const getDisabilityPlans = (state: EntityState, type: "short_term" | "long_term") => {
  const employeePolicy = getAnyEmployeePolicy(state);

  if (type === "short_term") {
    return employeePolicy.shortTermDisabilityPlan;
  }
  if (type === "long_term") {
    return employeePolicy.longTermDisabilityPlan;
  }

  return null;
};
export const getOfferedPlanTypes = (state: EntityState) => {
  const employeePolicy = getAnyEmployeePolicy(state);
  const planTypes = [];

  if (!employeePolicy.eligibleIndividualDental) {
    planTypes.push("dental");
  }

  if (!employeePolicy.eligibleIndividualVision) {
    planTypes.push("vision");
  }

  return planTypes;
};
